// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { Dictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { dataViewNotFound, getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { Draft } from "immer";
import { IBlogCollectionModel } from "@vanmeter/XDL/types";
import { IBlogListParams } from "@vanmeter/App/Services/BlogListService";

export type BlogsListDataView = {
    value?: IBlogCollectionModel;
    isLoading: boolean;
};

export type BlogsListState = {
    dataViews: Dictionary<BlogsListDataView>;
    params: IBlogListParams;
};

const initialBlogsListState: BlogsListState = {
    dataViews: {},
    params: {
        sortBy: "newestfirst",
        pageSize: 6,
        page: 1,
    },
};

const xBlogsList = createTypedReducerWithImmer(initialBlogsListState, {
    "xBlogsList/BeginLoading": (draft: Draft<BlogsListState>, action: { parameter: IBlogListParams }) => {
        const key = getDataViewKey(action.parameter);
        draft.dataViews[key] = { value: undefined, isLoading: true };
    },
    "xBlogsList/CompleteLoading": (
        draft: Draft<BlogsListState>,
        action: { parameter: IBlogListParams; blogs: IBlogCollectionModel },
    ) => {
        const key = getDataViewKey(action.parameter);
        draft.dataViews[key] = {
            value: action.blogs,
            isLoading: false,
        };
    },
    "xBlogsList/UpdateFields": (draft: Draft<BlogsListState>, action: { parameter: IBlogListParams }) => {
        draft.params = {
            ...draft.params,
            ...action.parameter,
        };
    },
});

(reducers as any).xBlogsList = xBlogsList;

export type xBlogsListState = {
    xBlogsList: BlogsListState;
} & ApplicationState;

export function getBlogsListDataView(state: xBlogsListState, parameters?: IBlogListParams): BlogsListDataView {
    const key = getDataViewKey(parameters || state.xBlogsList.params);
    const dataView = state.xBlogsList?.dataViews?.[key];
    if (dataView) {
        return dataView;
    }
    return dataViewNotFound as BlogsListDataView;
}

type xBlogsListReducers = {
    xBlogsList: typeof xBlogsList;
};

export type xBlogsListActions = Parameters<xBlogsListReducers[keyof xBlogsListReducers]>[1] | AnyAction;
